import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo, useEffect } from "react";
import { Search, ArrowRight, BookOpen, Clock, Tag, Sparkles, MoveUpRight } from "lucide-react";
import { SiteShell } from "@/components/SiteShell";
import { Button } from "@/components/ui/button";
import { ArticleCard } from "@/components/articles";
import { fetchArticles, type Article } from "@/lib/articles";
import { fetchAuthorProfile, getAuthorSocial } from "@/lib/author";

export const Route = createFileRoute("/articles/")({
  loader: async () => {
    const [articles, profile] = await Promise.all([fetchArticles(), fetchAuthorProfile()]);
    return { articles, profile };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: "AI Automation, RAG & Voice AI Articles | BuildWithJIT" },
      {
        name: "description",
        content: `Practical guides on RAG pipelines, n8n automation, voice AI agents, and MCP from real production projects by ${loaderData?.profile.name ?? "our authors"}.`,
      },
      {
        name: "robots",
        content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
      },
      {
        name: "googlebot",
        content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
      },
      { property: "og:site_name", content: "BuildWithJIT" },
      { property: "og:title", content: "AI Automation, RAG & Voice AI Articles | BuildWithJIT" },
      {
        property: "og:description",
        content: `Practical guides on RAG pipelines, n8n automation, voice AI agents, and MCP from real production projects by ${loaderData?.profile.name ?? "our authors"}.`,
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://buildwithjit.com/articles" },
      { property: "og:image", content: "https://buildwithjit.com/og-share.png" },
      { property: "og:image:secure_url", content: "https://buildwithjit.com/og-share.png" },
      { property: "og:image:type", content: "image/png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      {
        property: "og:image:alt",
        content: "AI Automation, RAG & Voice AI Articles | BuildWithJIT",
      },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@buildwithjit" },
      { name: "twitter:creator", content: "@buildwithjit" },
      { name: "twitter:title", content: "AI Automation, RAG & Voice AI Articles | BuildWithJIT" },
      {
        name: "twitter:description",
        content: `Practical guides on RAG pipelines, n8n automation, voice AI agents, and MCP from real production projects by ${loaderData?.profile.name ?? "our authors"}.`,
      },
      { name: "twitter:image", content: "https://buildwithjit.com/og-share.png" },
    ],
    links: [
      { rel: "canonical", href: "https://buildwithjit.com/articles" },
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/png", sizes: "48x48", href: "/icon-48.png" },
      { rel: "icon", type: "image/png", sizes: "96x96", href: "/icon-96.png" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/icon-192.png" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/icon-512.png" },
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      { rel: "shortcut icon", href: "/favicon.ico" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
    ],
  }),
  component: ArticlesIndexPage,
});

function ArticlesIndexPage() {
  const { articles: initialArticles, profile } = Route.useLoaderData();
  const social = getAuthorSocial(profile);
  const [articles, setArticles] = useState<Article[]>(initialArticles || []);
  const [query, setQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>(() => {
    if (typeof window !== "undefined") {
      const p = new URLSearchParams(window.location.search).get("category");
      return p ? p : "All";
    }
    return "All";
  });

  // Client-side hydration sync to guarantee latest database state
  useEffect(() => {
    let active = true;
    fetchArticles()
      .then((data) => {
        if (active && data && data.length > 0) {
          setArticles(data);
        }
      })
      .catch((err) => console.warn("Could not sync articles in client:", err));

    return () => {
      active = false;
    };
  }, []);

  // Sort articles newest-first by publication date
  const sortedArticles = useMemo(() => {
    return [...articles].sort((a: Article, b: Article) => {
      const timeA = new Date(a.date).getTime() || 0;
      const timeB = new Date(b.date).getTime() || 0;
      return timeB - timeA;
    });
  }, [articles]);

  // Extract unique categories dynamically
  const categories = useMemo(() => {
    const set = new Set<string>();
    sortedArticles.forEach((a: Article) => {
      const cat = (a.category || "").trim();
      if (cat) set.add(cat);
    });
    return ["All", ...Array.from(set)];
  }, [sortedArticles]);

  // Handle category change and sync URL
  const handleSelectCategory = (cat: string) => {
    setSelectedCategory(cat);
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      if (cat === "All") {
        url.searchParams.delete("category");
      } else {
        url.searchParams.set("category", cat);
      }
      window.history.replaceState({}, "", url.toString());
    }
  };

  // Filter articles based on category and search query
  const filteredArticles = useMemo(() => {
    return sortedArticles.filter((a: Article) => {
      const matchesCategory =
        selectedCategory === "All" ||
        (a.category && a.category.toLowerCase() === selectedCategory.toLowerCase());
      const q = query.trim().toLowerCase();
      const matchesQuery =
        !q ||
        a.title.toLowerCase().includes(q) ||
        a.excerpt.toLowerCase().includes(q) ||
        (a.category && a.category.toLowerCase().includes(q));
      return matchesCategory && matchesQuery;
    });
  }, [query, selectedCategory, sortedArticles]);

  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "AI Automation, RAG & Voice AI Articles",
    description: `Practical guides on RAG pipelines, n8n automation, voice AI agents, and MCP from real production projects by ${profile.name}.`,
    url: "https://buildwithjit.com/articles",
    publisher: {
      "@type": "Organization",
      name: "BuildWithJIT",
      url: "https://buildwithjit.com",
      sameAs: Object.values(social),
      logo: {
        "@type": "ImageObject",
        url: "https://buildwithjit.com/icon-512.png",
        width: 512,
        height: 512,
      },
    },
  };

  return (
    <SiteShell social={social}>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionSchema) }}
      />
      <div className="articles-archive-page">
        {/* Header Hero Section */}
        <section className="articles-hero">
          <div className="container-wide">
            <div className="eyebrow">
              <span className="eyebrow-line" /> THE BUILDWITHJIT TECHNICAL JOURNAL / ARCHIVE
            </div>
            <h1>
              AI Automation, RAG & Voice AI <em>Articles.</em>
            </h1>
            <p>
              Practical architecture deep dives, real-world benchmarks, and production-tested code
              on RAG pipelines, voice AI agents, and intelligent workflows.
            </p>
          </div>
        </section>

        {/* Filter and Content Grid */}
        <section className="articles-archive-body">
          <div className="container-wide">
            {/* Control Bar: Categories & Search */}
            <div className="archive-control-bar">
              <div className="archive-categories" role="tablist" aria-label="Article categories">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    role="tab"
                    aria-selected={selectedCategory === cat}
                    className={`archive-cat-btn ${selectedCategory === cat ? "is-active" : ""}`}
                    onClick={() => handleSelectCategory(cat)}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              <div className="archive-search">
                <Search size={16} className="search-icon" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search articles…"
                  aria-label="Search articles"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    className="search-clear-btn"
                    aria-label="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>

            {/* Results Counter */}
            <div className="archive-status-bar">
              <span className="results-count">
                Showing <strong>{filteredArticles.length}</strong>{" "}
                {filteredArticles.length === 1 ? "article" : "articles"}
                {selectedCategory !== "All" ? ` in ${selectedCategory}` : ""}
              </span>
            </div>

            {/* Articles Grid */}
            {filteredArticles.length > 0 ? (
              <div className="archive-grid">
                {filteredArticles.map((article) => (
                  <ArticleCard key={article.slug} article={article} />
                ))}
              </div>
            ) : (
              <div className="archive-empty-state">
                <p>No articles found matching your criteria.</p>
                <Button
                  variant="outline"
                  onClick={() => {
                    setQuery("");
                    handleSelectCategory("All");
                  }}
                  className="mt-4"
                >
                  Clear filters
                </Button>
              </div>
            )}
          </div>
        </section>
      </div>
    </SiteShell>
  );
}
